import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Link, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { QuotationService } from '../../services/quotation.service';
import {
  Quotation, QuotationStatus, QUOTATION_STATUS_COLORS,
} from '../../interfaces/sales/quotation.interface';
import { useToast } from '../../context/ToastContext';

const STATUS_COLORS = QUOTATION_STATUS_COLORS;

const QuotationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);

  // Send dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Reject dialog state — collects required reason (mirrors SalesInvoiceDetail void dialog)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Convert in-flight
  const [converting, setConverting] = useState(false);

  // Clone in-flight
  const [cloning, setCloning] = useState(false);

  // Email in-flight
  const [emailing, setEmailing] = useState(false);

  // PDF download in-flight
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const q = await QuotationService.getById(id);
      setQuotation(q);
    } catch (e) {
      toast.showApiError(e, 'Failed to load quotation');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send — returns updated quotation; use setState not redundant reload
  const handleSend = async () => {
    if (!quotation) return;
    setSending(true);
    try {
      const updated = await QuotationService.send(quotation.id);
      setQuotation(updated);
      toast.showSuccess('Quotation sent');
      setSendDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to send quotation');
    } finally {
      setSending(false);
    }
  };

  // Delete — navigates away on success; surface 400 via toast
  const handleDelete = async () => {
    if (!quotation) return;
    setDeleting(true);
    try {
      await QuotationService.remove(quotation.id);
      toast.showSuccess('Quotation deleted');
      navigate('/sales/quotations');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete quotation');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // Confirm — returns updated quotation; use setState
  const handleConfirm = async () => {
    if (!quotation) return;
    setConfirming(true);
    try {
      const updated = await QuotationService.confirm(quotation.id);
      setQuotation(updated);
      toast.showSuccess('Quotation confirmed');
      setConfirmDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to confirm quotation');
    } finally {
      setConfirming(false);
    }
  };

  // Reject — collects required reason via dialog; returns updated quotation; use setState
  const handleReject = async () => {
    if (!quotation || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      const updated = await QuotationService.reject(quotation.id, { reason: rejectReason.trim() });
      setQuotation(updated);
      toast.showSuccess('Quotation rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
    } catch (e) {
      toast.showApiError(e, 'Failed to reject quotation');
    } finally {
      setRejecting(false);
    }
  };

  // Convert → SalesInvoice; navigate to the new invoice
  const handleConvert = async () => {
    if (!quotation) return;
    setConverting(true);
    try {
      const invoice = await QuotationService.convert(quotation.id);
      toast.showSuccess('Quotation converted to invoice');
      navigate(`/sales/invoices/${invoice.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to convert quotation');
    } finally {
      setConverting(false);
    }
  };

  // Clone → new draft Quotation; navigate to the new draft
  const handleClone = async () => {
    if (!quotation) return;
    setCloning(true);
    try {
      const cloned = await QuotationService.clone(quotation.id);
      toast.showSuccess('Quotation cloned');
      navigate(`/sales/quotations/${cloned.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to clone quotation');
    } finally {
      setCloning(false);
    }
  };

  // Download PDF — Blob + in-flight guard (mirrors SalesInvoiceDetail)
  const handleDownloadPdf = async () => {
    if (!quotation) return;
    setDownloading(true);
    try {
      const blob = await QuotationService.downloadPdf(quotation.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${quotation.quotationNumber ?? quotation.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.showApiError(e, 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Email — in-flight guard (mirrors SalesInvoiceDetail)
  const handleEmail = async () => {
    if (!quotation) return;
    setEmailing(true);
    try {
      await QuotationService.email(quotation.id);
      toast.showSuccess('Quotation emailed successfully');
    } catch (e) {
      toast.showApiError(e, 'Failed to email quotation');
    } finally {
      setEmailing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!quotation) return <Box sx={{ p: 3 }}><Typography>Quotation not found.</Typography></Box>;

  const status: QuotationStatus = quotation.status;

  // Status-gated action map (mirrors SalesInvoiceDetail `can`)
  const can = {
    send: status === 'DRAFT',
    edit: status === 'DRAFT',
    delete: status === 'DRAFT',
    confirm: status === 'SENT',
    reject: status === 'SENT',
    clone: status === 'SENT' || status === 'CONFIRMED' || status === 'REJECTED' || status === 'EXPIRED',
    convert: status === 'CONFIRMED',
    // PDF/email hidden for DRAFT (no quotation number yet)
    pdf: status !== 'DRAFT',
    email: status !== 'DRAFT',
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Title + action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {quotation.quotationNumber ?? 'Draft Quotation'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {can.send && (
            <Button variant="contained" color="primary" onClick={() => setSendDialogOpen(true)}>
              Send
            </Button>
          )}
          {can.edit && (
            <Button variant="outlined" onClick={() => navigate(`/sales/quotations/${quotation.id}/edit`)}>
              Edit
            </Button>
          )}
          {can.delete && (
            <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          {can.confirm && (
            <Button variant="contained" color="success" onClick={() => setConfirmDialogOpen(true)}>
              Confirm
            </Button>
          )}
          {can.reject && (
            <Button variant="outlined" color="error" onClick={() => setRejectDialogOpen(true)}>
              Reject
            </Button>
          )}
          {can.convert && (
            <Button variant="contained" color="primary" onClick={handleConvert} disabled={converting}>
              {converting ? <CircularProgress size={20} /> : 'Convert to Invoice'}
            </Button>
          )}
          {can.clone && (
            <Button variant="outlined" onClick={handleClone} disabled={cloning}>
              {cloning ? <CircularProgress size={20} /> : 'Clone'}
            </Button>
          )}
          {can.pdf && (
            <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} /> : 'Download PDF'}
            </Button>
          )}
          {can.email && (
            <Button variant="outlined" onClick={handleEmail} disabled={emailing}>
              {emailing ? <CircularProgress size={20} /> : 'Email'}
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      {/* Header info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Quotation Number</Typography>
            <Typography>{quotation.quotationNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={status} color={STATUS_COLORS[status]} size="small" />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Customer</Typography>
            <Typography>{quotation.customerName ?? quotation.customerId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Quotation Date</Typography>
            <Typography>{quotation.quotationDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Valid Until</Typography>
            <Typography>{quotation.validUntil}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography>{quotation.subtotal.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
            <Typography>{quotation.taxAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography fontWeight="bold">{quotation.totalAmount.toFixed(2)}</Typography>
          </Box>
          {quotation.notes && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography>{quotation.notes}</Typography>
            </Box>
          )}
          {status === 'REJECTED' && quotation.rejectReason && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Reject Reason</Typography>
              <Typography color="error.main">{quotation.rejectReason}</Typography>
            </Box>
          )}
          {status === 'CONVERTED' && quotation.convertedInvoiceId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Converted Invoice</Typography>
              <Link
                component={RouterLink}
                to={`/sales/invoices/${quotation.convertedInvoiceId}`}
              >
                View Invoice
              </Link>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Line items table */}
      {quotation.lines.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Tax %</TableCell>
                <TableCell align="right">Line Tax</TableCell>
                <TableCell align="right">Line Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotation.lines.map(line => (
                <TableRow key={line.id}>
                  <TableCell>{line.description}</TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell align="right">{line.unitPrice.toFixed(2)}</TableCell>
                  <TableCell align="right">{line.taxRatePct}%</TableCell>
                  <TableCell align="right">{line.lineTax?.toFixed(2) ?? '—'}</TableCell>
                  <TableCell align="right">{line.lineTotal?.toFixed(2) ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Send confirm dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
        <DialogTitle>Send this quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sending the quotation will assign a quotation number and mark it as sent.
            You can still confirm, reject, or clone it after sending.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)} disabled={sending}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSend} disabled={sending}>
            {sending ? <CircularProgress size={20} /> : 'Confirm Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this draft quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the draft quotation. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm quotation dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm this quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirming the quotation marks it as accepted. You can then convert it to an invoice.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={confirming}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConfirm} disabled={confirming}>
            {confirming ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog — collects required reason (mirrors SalesInvoiceDetail void dialog) */}
      <Dialog open={rejectDialogOpen} onClose={() => { setRejectDialogOpen(false); setRejectReason(''); }} fullWidth maxWidth="sm">
        <DialogTitle>Reject this quotation?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Rejecting the quotation marks it as declined. A reject reason is required.
          </DialogContentText>
          <TextField
            label="Reject Reason"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setRejectDialogOpen(false); setRejectReason(''); }}
            disabled={rejecting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejecting || rejectReason.trim().length === 0}
          >
            {rejecting ? <CircularProgress size={20} /> : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default QuotationDetail;
